import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  Info,
  X,
  Check,
  CheckCheck,
  Lightbulb,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../../utils/api-base';
import { getWebSocketClient } from '../../utils/websocket-client';

interface AlarmNotificationsProps {
  clientId: string;
  token: string;
  username: string;
}

export function AlarmNotifications({ clientId, token, username }: AlarmNotificationsProps) {
  const [alarms, setAlarms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchActiveAlarms();
    const interval = setInterval(fetchActiveAlarms, 30000); // Refresh every 30 seconds (reduced from 10s to avoid excessive polling)

    const ws = getWebSocketClient(token, { clientId });
    const unsubTriggered = ws.on('alarm_triggered', () => fetchActiveAlarms());
    const unsubResolved = ws.on('alarm_resolved', () => fetchActiveAlarms());

    return () => {
      clearInterval(interval);
      unsubTriggered();
      unsubResolved();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchActiveAlarms = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/clients/${clientId}/active-alarms`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAlarms(data.alarms || []);
      }
    } catch (error) {
      console.error('Error fetching alarms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alarmId: string) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/alarms/${alarmId}/acknowledge`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ clientId, userId: username }),
        }
      );

      if (response.ok) {
        toast.success('Alarm acknowledged');
        fetchActiveAlarms();
      }
    } catch (error) {
      console.error('Error acknowledging alarm:', error);
      toast.error('Failed to acknowledge alarm');
    }
  };

  const handleResolve = async (alarmId: string) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/alarms/${alarmId}/resolve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ clientId }),
        }
      );

      if (response.ok) {
        toast.success('Alarm resolved');
        fetchActiveAlarms();
      }
    } catch (error) {
      console.error('Error resolving alarm:', error);
      toast.error('Failed to resolve alarm');
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'info':
        return <Info className="h-5 w-5 text-blue-500" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'info':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const activeAlarms = alarms.filter(a => !a.resolved);
  const displayedAlarms = showAll ? activeAlarms : activeAlarms.slice(0, 5);
  const criticalCount = activeAlarms.filter(a => a.severity === 'critical').length;
  const warningCount = activeAlarms.filter(a => a.severity === 'warning').length;

  if (loading) {
    return null;
  }

  if (activeAlarms.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <Card className="border-l-4 border-l-red-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <Bell className="h-5 w-5 text-red-500 animate-pulse" />
              </div>
              <div>
                <h3 className="font-semibold">Active Alarms</h3>
                <p className="text-sm text-muted-foreground">
                  {criticalCount > 0 && `${criticalCount} Critical`}
                  {criticalCount > 0 && warningCount > 0 && ', '}
                  {warningCount > 0 && `${warningCount} Warning`}
                </p>
              </div>
            </div>
            <Badge variant="destructive" className="text-lg px-3 py-1">
              {activeAlarms.length}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Alarm List */}
      <div className="space-y-2">
        <AnimatePresence>
          {displayedAlarms.map((alarm) => (
            <motion.div
              key={alarm.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              <Card className={`border-l-4 ${alarm.severity === 'critical' ? 'border-l-red-500' : alarm.severity === 'warning' ? 'border-l-yellow-500' : 'border-l-blue-500'}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getSeverityIcon(alarm.severity)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getSeverityColor(alarm.severity)}>
                              {alarm.severity.toUpperCase()}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {alarm.plcName} - {alarm.tagName}
                            </span>
                          </div>
                          <p className="font-medium text-sm">{alarm.message}</p>
                          {alarm.rootCauseHint && (
                            <p className="text-xs text-muted-foreground italic flex items-start gap-1 mt-1">
                              <Lightbulb className="h-3 w-3 shrink-0 mt-0.5" />
                              {alarm.rootCauseHint}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>
                            {new Date(alarm.triggeredAt).toLocaleString()}
                          </span>
                          {alarm.acknowledgedAt && (
                            <span className="flex items-center gap-1">
                              <Check className="h-3 w-3" />
                              Ack by {alarm.acknowledgedBy}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          {!alarm.acknowledgedAt && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1 h-7 text-xs"
                              onClick={() => handleAcknowledge(alarm.id)}
                            >
                              <Check className="h-3 w-3" />
                              Acknowledge
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="gap-1 h-7 text-xs"
                            onClick={() => handleResolve(alarm.id)}
                          >
                            <CheckCheck className="h-3 w-3" />
                            Resolve
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>

        {activeAlarms.length > 5 && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Show Less' : `Show All ${activeAlarms.length} Alarms`}
          </Button>
        )}
      </div>
    </div>
  );
}
